import React, { useState, useEffect, useRef } from 'react';
import { useScreenState } from '../lib/screenState';
import { apiClient, errorMessage } from '../api';
import { cardClasses, checkboxClasses, inputClasses, primaryButton } from '../components/ui';
import { formatTime } from '../lib/dates';
import { outdatedCount } from '../lib/outdated';
import OperationErrors from './OperationErrors';

const POLL_MS = 2000;
const selectAllButton = 'text-sm font-semibold text-zafiro-600 hover:underline disabled:text-slate-400 disabled:hover:no-underline disabled:cursor-not-allowed';

// Sincronización: corre en segundo plano en el servidor; acá se elige qué versiones entran, se lanza y se sigue el avance.
// `versions` son las activas: una versión desactivada no se sincroniza y no figura.
export const SyncCard = ({ versions, addToast, onFinished }) => {
    const [skipped, setSkipped] = useState([]);
    const [full, setFull] = useState(false);
    const [status, setStatus] = useState({ running: false });
    const timer = useRef(null);

    const poll = async (announce) => {
        try {
            const current = (await apiClient.get('/api/sync-status', { silent: true })).data;
            setStatus(current);
            if (current.running) { timer.current = setTimeout(() => poll(true), POLL_MS); return; }
            if (announce) {
                addToast(current.error || current.result || 'Sincronización terminada.', current.error ? 'error' : 'success');
                onFinished();
            }
        } catch (e) { /* sin permiso o sin conexión: se deja el último estado */ }
    };

    useEffect(() => {
        apiClient.get('/api/sync-config').then(res => setSkipped(res.data.skipped_versions)).catch(() => {});
        poll(false); // si alguien dejó una corriendo, se engancha al avance
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveSkipped = async (next, failure) => {
        setSkipped(next);
        try { await apiClient.put('/api/sync-config', { skipped_versions: next }); }
        catch (e) { setSkipped(skipped); addToast(errorMessage(e, failure), 'error'); }
    };
    const toggleVersion = (name) => saveSkipped(skipped.includes(name) ? skipped.filter(v => v !== name) : [...skipped, name],
        `No se pudo guardar el cambio de ${name}: la casilla vuelve a como estaba.`);
    // Tildar o destildar todas de una vez: un solo pedido, con la lista completa.
    const setAll = (checked) => saveSkipped(checked ? [] : versions.map(v => v.id), 'No se pudo guardar el cambio: las casillas vuelven a como estaban.');

    const start = async () => {
        try {
            await apiClient.post('/api/sync-files', { full });
            setStatus({ running: true, step: 'Iniciando' });
            timer.current = setTimeout(() => poll(true), POLL_MS);
        } catch (e) { addToast(errorMessage(e, 'No se pudo iniciar la sincronización.'), 'error'); }
    };

    const included = versions.filter(v => !skipped.includes(v.id));
    return (
        <div className={cardClasses}>
            <h3 className="font-semibold text-lg tracking-tight mb-2 text-slate-900">Sincronización del Servidor</h3>
            <p className="text-sm text-slate-600 mb-4">Registra en la base lo que se copió a mano a las carpetas del servidor y detecta zips reemplazados. Los zips que no cambiaron no se vuelven a leer.</p>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                <p className="text-sm font-semibold text-slate-700">Versiones a sincronizar <span className="font-normal text-slate-500">(las destildadas se saltean; la elección queda guardada)</span></p>
                <button type="button" onClick={() => setAll(true)} disabled={included.length === versions.length} className={selectAllButton}>Seleccionar todas</button>
                <button type="button" onClick={() => setAll(false)} disabled={included.length === 0} className={selectAllButton}>Deseleccionar todas</button>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
                {versions.map(v => (<label key={v.id} className="flex items-center space-x-2 cursor-pointer text-sm"><input type="checkbox" className={checkboxClasses} checked={!skipped.includes(v.id)} onChange={() => toggleVersion(v.id)} /><span className="font-mono">{v.name}</span></label>))}
            </div>
            <label className="flex items-center space-x-2 cursor-pointer text-sm text-slate-600 mb-4"><input type="checkbox" className={checkboxClasses} checked={full} onChange={() => setFull(f => !f)} /><span>Sincronización completa (volver a leer todos los zips; más lenta)</span></label>
            <div className="flex items-center gap-4">
                <button onClick={start} disabled={status.running || included.length === 0} className={primaryButton}>{status.running ? 'Sincronizando...' : `Sincronizar ${included.length === versions.length ? 'todas las versiones' : included.map(v => v.name).join(', ')}`}</button>
                {status.running && <span className="text-sm text-slate-600">{status.step}</span>}
            </div>
            {!status.running && (status.result || status.error) && <p className={`mt-4 text-sm p-3 rounded-lg ${status.error ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-700'}`}><span className="font-semibold">Última corrida: </span>{status.error || status.result}</p>}
        </div>
    );
};

const REPORT_SECTIONS = [
    ['outdated_masters', 'Copias publicadas distintas del zip de test/ (los clientes bajan una versión vieja)', 'Se republican desde la pestaña Republicación.'],
    ['missing_links', 'Publicados sin symlink (o con symlink roto)', 'Se reparan.'],
    ['missing_masters', 'Copias maestras faltantes', 'Se reparan si el zip de origen existe.'],
    ['source_missing', 'Updates registrados sin zip en test/', 'Sólo se informa.'],
    ['orphan_masters', 'Copias maestras que ningún cliente usa', 'Sólo se informa.'],
    ['unregistered_links', 'Zips en carpetas de clientes que la base no conoce', 'Se registran con una sincronización.'],
];

// Integridad: compara la base con el disco y repara lo reparable. Lo desactualizado se republica desde la pestaña Republicación.
// `refreshKey` cambia cuando algo pudo dejar viejo el informe (sincronizar, reparar): si hay uno a la vista se vuelve a
// pedir solo.
export const IntegrityCard = ({ versions, addToast, onChanged, refreshKey = 0 }) => {
    const [version, setVersion] = useScreenState('admin.integrity.version', versions[0]?.id || '');
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);
    // Lo que no se pudo reparar: queda a la vista hasta que se vuelve a revisar o reparar a mano.
    const [failed, setFailed] = useState(null);
    // Sin versión elegida, o con una que ya no se ofrece (se desactivó), vale la primera.
    useEffect(() => { if (versions.length > 0 && !versions.some(v => v.id === version)) { setVersion(versions[0].id); setData(null); } }, [versions, version, setVersion]);

    const run = async (method, keepErrors = false) => {
        setBusy(true);
        if (!keepErrors) setFailed(null);
        try {
            const res = await apiClient.request({ url: `/api/integrity/${version}`, method });
            setData(res.data);
            setCheckedAt(new Date());
            if (method === 'post' && res.data.errors.length > 0) setFailed({ errors: res.data.errors, total: res.data.errors_total });
            if (method === 'post') { addToast(`${res.data.repaired} elemento(s) reparados${res.data.errors.length > 0 ? `, ${res.data.errors.length} con error` : ''}.`, res.data.errors.length > 0 ? 'warning' : 'success'); onChanged(); }
        } catch (e) { addToast(errorMessage(e), 'error'); } finally { setBusy(false); }
    };
    const shown = useRef(false);
    shown.current = Boolean(data);
    useEffect(() => {
        if (shown.current) run('get', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    const repairable = data ? data.counts.missing_links + data.counts.missing_masters : 0;
    return (
        <div className={cardClasses}>
            <h3 className="font-semibold text-lg tracking-tight mb-2 text-slate-900">Integridad</h3>
            <p className="text-sm text-slate-600 mb-4">Compara lo que la base dice que está publicado con lo que hay en las carpetas del servidor. Nunca borra archivos.</p>
            <div className="flex flex-wrap items-center gap-3">
                <select value={version} onChange={e => { setVersion(e.target.value); setData(null); setFailed(null); }} className={`${inputClasses} font-mono`}>{versions.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
                <button onClick={() => run('get')} disabled={busy || !version} className={primaryButton}>{busy ? 'Revisando...' : 'Revisar integridad'}</button>
                {repairable > 0 && <button onClick={() => run('post')} disabled={busy} className={primaryButton}>Reparar {repairable}</button>}
            </div>
            {failed && <div className="mt-4"><OperationErrors {...failed} /></div>}
            {data && checkedAt && <p className="mt-4 text-xs text-slate-500">Informe de las {formatTime(checkedAt)}. Se vuelve a pedir solo al terminar de sincronizar o de reparar.</p>}
            {data && (
                <ul className="mt-1 divide-y divide-slate-100 border border-slate-200 rounded-lg text-sm">
                    {REPORT_SECTIONS.map(([key, label, hint]) => (
                        <li key={key} className="px-3 py-2">
                            <details>
                                <summary className="cursor-pointer flex justify-between"><span>{label} <span className="text-slate-400">· {hint}</span></span><span className={`flex-shrink-0 pl-3 font-semibold ${data.counts[key] > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{key === 'outdated_masters' && data.outdated ? outdatedCount(data.outdated.files, data.outdated.clients) : data.counts[key]}</span></summary>
                                {data.counts[key] > 0 && <ul className="mt-2 max-h-40 overflow-y-auto font-mono text-xs text-slate-600">{data.report[key].map((item, i) => <li key={i}>{typeof item === 'string' ? item : `${item.client} / ${item.filename}`}</li>)}</ul>}
                            </details>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// Duplicados: el mismo zip registrado dos veces o la misma publicación dos veces para un cliente. Se unifican solos
// al arrancar y en cada sincronización; acá se hace a pedido y queda el resultado a la vista.
export const DuplicatesCard = ({ addToast, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!window.confirm('Se van a unificar los registros repetidos de la base: un update registrado dos veces, o publicado dos veces para un mismo cliente. De cada repetido queda uno solo.\n\nSólo se borran registros de la base: no se borra ni se mueve ningún archivo.\n\n¿Continuar?')) return;
        setBusy(true);
        try {
            const { data } = await apiClient.post('/api/dedupe');
            setResult(data);
            addToast(data.message, data.missing_indexes.length > 0 ? 'warning' : 'success');
            if (data.merged_files + data.removed_publications > 0) onChanged();
        } catch (e) { addToast(errorMessage(e, 'No se pudieron unificar los duplicados.'), 'error'); } finally { setBusy(false); }
    };

    return (
        <div className={cardClasses}>
            <h3 className="font-semibold text-lg tracking-tight mb-2 text-slate-900">Duplicados</h3>
            <p className="text-sm text-slate-600 mb-4">Unifica los registros repetidos de la base, como un update que aparece dos veces en Publicados de un cliente. Nunca borra archivos. También corre sola al arrancar y en cada sincronización.</p>
            <button onClick={run} disabled={busy} className={primaryButton}>{busy ? 'Unificando...' : 'Unificar duplicados'}</button>
            {result && <p className={`mt-4 text-sm p-3 rounded-lg ${result.missing_indexes.length > 0 ? 'bg-amber-50 text-amber-800' : 'bg-slate-50 text-slate-700'}`}><span className="font-semibold">Resultado: </span>{result.message}</p>}
        </div>
    );
};
